import styled from "styled-components/macro";
import {
  TextField,
  Select,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { DeviceType } from "../../utils/hooks/useDeviceHook";
import { themePalette } from "../../theme";

// Reused from Landing.styles.tsx
export const LandingPageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PublicConsentPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

export const LandingGeneralContainer = styled.div<{ padding?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${themePalette.white};
  border-radius: 24px;
  margin-bottom: 24px;
  box-shadow: 0px 6px 11px 0px rgba(0, 0, 0, 0.04);
  padding: ${({ padding }) => padding || "24px"};
  width: 100%;
`;

export const LandingPageAccordionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const LandingPageTitleWrapper = styled.div`
  display: flex;
  align-self: flex-start;
  margin-bottom: 16px;
`;

// New styles for Public Consent
export const PublicConsentWrapper = styled.div<{ deviceType: DeviceType }>`
  padding: ${({ deviceType }) => {
    if (deviceType === "mobile") return "16px";
    if (deviceType === "tablet") return "24px";
    return "40px";
  }};
  max-width: ${({ deviceType }) =>
    deviceType === "desktop" ? "1200px" : "100%"};
  margin: 0 auto;
  min-height: 100vh;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const PublicConsentPageHeader = styled.div`
  height: 100px;
  background: ${themePalette.loginHeroGradientMobile};
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

export const DesktopContentGrid = styled.div<{ deviceType: DeviceType }>`
  display: ${({ deviceType }) => (deviceType === "desktop" ? "grid" : "flex")};
  grid-template-columns: ${({ deviceType }) =>
    deviceType === "desktop" ? "1fr 400px" : "none"};
  gap: ${({ deviceType }) => (deviceType === "desktop" ? "40px" : "0")};
  flex-direction: ${({ deviceType }) =>
    deviceType === "desktop" ? "row" : "column"};
  align-items: ${({ deviceType }) =>
    deviceType === "desktop" ? "start" : "stretch"};
  max-width: 100%;
  overflow-x: hidden;

  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  ${({ deviceType }) =>
    deviceType === "mobile" &&
    `
    gap: 20px;
  `}
`;

export const FormSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const VideoSectionWrapper = styled.div<{ deviceType: DeviceType }>`
  display: flex;
  flex-direction: column;
  gap: ${({ deviceType }) => (deviceType === "mobile" ? "20px" : "32px")};

  ${({ deviceType }) =>
    deviceType === "desktop" &&
    `
    position: sticky;
    top: 0px;
    height: fit-content;
    min-width: 400px;
  `}

  ${({ deviceType }) =>
    deviceType === "mobile" &&
    `
    max-width: 100%;
    overflow: hidden;
    padding: 0 0 0 0;
  `}
`;

export const PublicConsentHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

export const PublicConsentIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #5ec3b6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  box-shadow: 0 4px 12px rgba(94, 195, 182, 0.2);

  svg {
    width: 40px;
    height: 40px;
    fill: white;
  }

  img {
    filter: brightness(0) invert(1);
  }
`;

export const ConsentFormContainer = styled.div`
  background: ${themePalette.white};
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 32px;
  box-shadow: 0px 6px 11px 0px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 16px;
    margin-bottom: 24px;
  }
`;

export const ConsentText = styled.div`
  margin-bottom: 32px;
  line-height: 1.6;
  color: ${themePalette.greyMedium};
`;

export const VideoPlayersContainer = styled.div`
  padding: 20px 0;
  width: 100%;
  box-sizing: border-box;
`;

export const ConsentVideoWrapper = styled.div<{
  deviceType: DeviceType;
  hasBottomMargin?: boolean;
}>`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  aspect-ratio: ${({ deviceType }) =>
    deviceType === "desktop" ? "16/9" : "auto"};
  border-radius: 12px;
  margin-bottom: ${({ hasBottomMargin }) => (hasBottomMargin ? "16px" : "0")};

  & > div {
    width: 100% !important;
    max-width: 100% !important;
    ${({ deviceType }) =>
      deviceType === "desktop" &&
      `
      height: 100% !important;
    `}
  }

  & .react-player {
    width: 100% !important;
    max-width: 100% !important;
    ${({ deviceType }) =>
      deviceType === "desktop" &&
      `
      height: 100% !important;
    `}
  }

  & .react-player > div {
    width: 100% !important;
    ${({ deviceType }) =>
      deviceType === "desktop" &&
      `
      height: 100% !important;
    `}
  }

  & .react-player__preview {
    width: 100% !important;
    max-width: 100% !important;
    ${({ deviceType }) =>
      deviceType === "desktop" &&
      `
      height: 100% !important;
    `}
  }

  & .react-player__preview img {
    width: 100% !important;
    max-width: 100% !important;
    ${({ deviceType }) =>
      deviceType === "desktop"
        ? `
      height: 100% !important;
      object-fit: cover;
    `
        : `
      height: auto;
    `}
  }

  & img {
    max-width: 100% !important;
    height: auto;
  }
`;

export const ConsentMarkdownContainer = styled.div`
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background-color: ${themePalette.neutral50};
  margin-bottom: 24px;
`;

export const ConsentCheckboxContainer = styled.div`
  margin-bottom: 24px;
`;

export const ConsentCheckboxContent = styled.div`
  padding-left: 8px;
`;

export const ConsentCheckbox = styled(Checkbox)`
  && {
    color: #e57373;
    &.Mui-checked {
      color: #e57373;
    }
  }
`;

export const ConsentCheckboxLabel = styled(FormControlLabel)`
  && {
    display: block;
    margin-bottom: 8px;
  }
`;

export const FormGrid = styled.div<{ deviceType: DeviceType }>`
  display: grid;
  grid-template-columns: ${({ deviceType }) =>
    deviceType === "mobile" ? "1fr" : "1fr 1fr"};
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

export const SignatureNoteContainer = styled.div<{ deviceType: DeviceType }>`
  grid-column: ${({ deviceType }) =>
    deviceType === "mobile" ? "auto" : "1 / -1"};
  margin-top: -20px;
`;

export const ConsentAcknowledgementContainer = styled.div`
  margin-bottom: 16px;
`;

export const FormField = styled(TextField)`
  && {
    .MuiInputBase-root {
      border-radius: 12px;
      background: ${themePalette.neutral50};
      border: 1px solid #e2e8f0;
      min-height: 56px;
    }

    .MuiInputBase-input {
      font-size: 16px;
      color: #2d3748;
      padding: 16px;

      &::placeholder {
        color: #a0aec0;
        opacity: 1;
      }
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }

    &.Mui-focused .MuiInputBase-root {
      border-color: ${themePalette.greenSuccess};
      box-shadow: 0 0 0 3px rgba(96, 198, 106, 0.1);
    }

    .MuiFormHelperText-root {
      color: #e53e3e;
      font-size: 14px;
      margin-top: 8px;
      margin-left: 0;
    }

    .MuiInputLabel-shrink {
      padding: 0 4px;
      transform: translate(14px, -15px) scale(0.75);
    }
  }
`;

export const GenderSelect = styled(Select<string>)`
  && {
    background: ${themePalette.neutral50};
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    min-height: 56px;

    .MuiSelect-select {
      font-size: 16px;
      color: #2d3748;
      padding: 16px;
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }

    &.Mui-focused {
      border-color: #5ec3b6;
      box-shadow: 0 0 0 3px rgba(94, 195, 182, 0.1);
    }
  }
`;

export const DateField = styled(TextField)`
  && {
    .MuiInputBase-root {
      border-radius: 12px;
      background: ${themePalette.neutral50};
      border: 1px solid #e2e8f0;
      min-height: 56px;
    }

    .MuiInputBase-input {
      font-size: 16px;
      color: #2d3748;
      padding: 16px;
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }

    &.Mui-focused .MuiInputBase-root {
      border-color: ${themePalette.greenSuccess};
      box-shadow: 0 0 0 3px rgba(96, 198, 106, 0.1);
    }

    .MuiFormHelperText-root {
      color: #e53e3e;
      font-size: 14px;
      margin-top: 8px;
      margin-left: 0;
    }

    .MuiInputLabel-shrink {
      padding: 0 4px;
      transform: translate(14px, -15px) scale(0.75);
    }
  }
`;

export const ConsentButton = styled(Button)`
  && {
    background: #5ec3b6;
    color: white;
    font-size: 16px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 0 32px;
    border-radius: 22px;
    height: 44px;
    width: 100%;
    border: none;
    box-shadow: 0 4px 12px rgba(94, 195, 182, 0.3);

    &:hover {
      background: #4db5a8;
      box-shadow: 0 6px 16px rgba(94, 195, 182, 0.4);
    }

    &:disabled {
      background: #e2e8f0;
      color: #a0aec0;
      box-shadow: none;
    }
  }
`;

export const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
`;

export const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #5ec3b6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(94, 195, 182, 0.2);

  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`;

export const SuccessText = styled.div`
  color: #2d3748;
  line-height: 1.6;
  max-width: 600px;
`;

export const ErrorText = styled.div`
  color: #e53e3e;
  font-size: 14px;
  margin-top: 8px;
  text-align: left;
`;

export const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  .loading-text {
    font-size: 16px;
    color: ${themePalette.greyMedium};
  }
`;
